import React from "react";
import { useIntl } from "react-intl";
import { messages as m } from "./messages";
import styled from "styled-components";
import { Icon, PrimaryButton } from "@fluentui/react";
import { ITheme } from "cayo.ui";

interface HostedLicenseExpiredProps {
  expiredOn: string;
  daysOverdue: number;
}

export const HostedLicenseExpired: React.FC<HostedLicenseExpiredProps> = ({
  expiredOn,
  daysOverdue,
}) => {
  const intl = useIntl();

  return (
    <Page>
      <Container>
        <ClockWrapper>
          <ClockIcon iconName="Clock" />
        </ClockWrapper>

        <Title>{intl.formatMessage(m.title)}</Title>

        <Subtitle>{intl.formatMessage(m.subtitle)}</Subtitle>

        <InfoBox>
          <Row>
            <TableLabel>{intl.formatMessage(m.tableTitle)}</TableLabel>
            <Value></Value>
          </Row>

          <Row>
            <Label>{intl.formatMessage(m.expiredOnLabel)}</Label>
            <Value>{expiredOn}</Value>
          </Row>

          <Row>
            <Label>{intl.formatMessage(m.daysOverdueLabel)}</Label>
            <DangerValue>
              {intl.formatMessage(m.daysOverdueValue, { days: daysOverdue })}
            </DangerValue>
          </Row>
        </InfoBox>

        <Button href="https://www.cayosoft.com/about/contact-us/" target="_blank" rel="noreferrer">
          {intl.formatMessage(m.contactCayosoft)}
        </Button>

        <SupportText>
          {intl.formatMessage(m.needHelp)}{" "}
          <a href="https://www.cayosoft.com/support/" target="_blank" rel="noreferrer">
            {intl.formatMessage(m.contactSupport)}
          </a>
        </SupportText>
      </Container>
    </Page>
  );
};

const Page = styled.div`
  height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const Container = styled.div`
  text-align: center;
  max-width: 420px;
  width: 100%;
`;

const ClockWrapper = styled.div`
  margin-bottom: 0;
`;

const ClockIcon = styled(Icon)`
  font-size: 42px;
  margin: 0 auto;
  color: ${(props: { iconName: string; theme: ITheme }) =>
    props.theme.officeTheme.palette.themePrimary};
`;

const Title = styled.h2`
  font-size: 24px;
  margin: 5px 0 10px 0;
  font-weight: 600;
`;

const Subtitle = styled.p`
  font-size: 14px;
  line-height: 1.5;
  margin-bottom: 28px;
`;

const InfoBox = styled.div`
  background: ${(props) => (props.theme as ITheme).officeTheme.palette.neutralLighter};
  padding: 20px;
  border-radius: 6px;
  margin-bottom: 28px;
  text-align: left;
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 12px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const TableLabel = styled.span`
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 10px;
`;

const Label = styled.span`
  color: ${(props) => (props.theme as ITheme).cayoTheme.brandColors.disabled};
  font-size: 14px;
`;

const Value = styled.span`
  font-size: 14px;
  font-weight: 500;
`;

const DangerValue = styled(Value)`
  color: ${(props) => (props.theme as ITheme).cayoTheme.brandColors.error};
`;

const Button = styled(PrimaryButton)`
  width: 160px;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
  margin-bottom: 16px;
`;

const SupportText = styled.div`
  font-size: 12px;
  color: ${(props) => (props.theme as ITheme).cayoTheme.brandColors.disabled};

  a {
    color: ${(props) => (props.theme as ITheme).officeTheme.palette.themePrimary};
    text-decoration: none;
  }
`;
